import { Injectable, NgZone } from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '../auth.models';

// ID-5: the app logs in at Pro Pool ID with Authorization Code + PKCE, as a public client (no secret). The web build
// sends the page itself to Pro Pool ID, which returns it on /auth/callback (idCallbackGuard). Native apps open the
// system browser and get the custom-scheme redirect back through appUrlOpen. Pro Pool ID's endpoints are called
// with fetch, so the API's interceptors (bearer token, location, error alerts) never touch them.
const CLIENT_ID = 'propool-app';
const NATIVE_REDIRECT = 'com.cronair.propoolapp://callback';
const NATIVE_LOGOUT_REDIRECT = 'com.cronair.propoolapp://logout';
// The state and PKCE verifier of the login in progress, needed again when Pro Pool ID sends the browser back.
const PENDING_LOGIN = 'propool_id_pending_login';

/** An error answer from Pro Pool ID's token endpoint; `error` is the OAuth code, e.g. invalid_grant. */
export class IdTokenError extends Error {
	constructor(public error: string) {
		super(`Pro Pool ID token request failed: ${error}`);
	}
}

/** True when the access token expires within a minute (or can't be read): time to use the refresh token. */
export function expiresSoon(accessToken: string, now = Date.now()): boolean {
	try {
		const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
		return payload.exp * 1000 - now < 60_000;
	} catch {
		return false;
	}
}

@Injectable({ providedIn: 'root' })
export class IdAuthService {
	private redirectListener: PluginListenerHandle | null = null;
	private accountPageClosed: PluginListenerHandle | null = null;

	constructor(private zone: NgZone) {
	}

	private get native(): boolean {
		return Capacitor.isNativePlatform();
	}

	private get redirectUri(): string {
		return this.native ? NATIVE_REDIRECT : `${environment.currentHost}/auth/callback`;
	}

	/**
	 * Sends the user to Pro Pool ID's login, or with `register` to its registration (ID-6B): that signs the new account
	 * in and continues to the same authorize request, so both come back as a login. Web: the page navigates away and
	 * this never resolves to anything useful. Native: resolves with the callback URL once Pro Pool ID redirects back.
	 */
	async startLogin(register = false): Promise<string | null> {
		const state = randomString();
		const verifier = randomString();
		sessionStorage.setItem(PENDING_LOGIN, JSON.stringify({ state, verifier }));
		const query = new URLSearchParams({
			client_id: CLIENT_ID,
			redirect_uri: this.redirectUri,
			response_type: 'code',
			scope: 'openid profile email offline_access',
			state,
			code_challenge: await codeChallenge(verifier),
			code_challenge_method: 'S256',
		});
		const authorize = new URL(`connect/authorize?${query}`, environment.idHost);
		// Pro Pool ID only follows a local ReturnUrl.
		return this.open(register
			? `${environment.idHost}Account/Register?${new URLSearchParams({ ReturnUrl: authorize.pathname + authorize.search })}`
			: authorize.href, NATIVE_REDIRECT);
	}

	/**
	 * Opens one of Pro Pool ID's own account pages - profile, password, e-mail verification, password reset (ID-6B, ID-10) -
	 * in the system browser; the app has no screens of its own for those. `closed` runs once that browser is closed:
	 * the only moment the app can know something changed there (app.component.ts's resume listener covers the rest).
	 */
	async openAccountPage(path: string, closed?: () => void): Promise<void> {
		await this.accountPageClosed?.remove();
		this.accountPageClosed = null;
		if (closed) {
			// Capacitor's native bridge fires this outside Angular's zone, like Camera/Geolocation elsewhere in this app.
			this.accountPageClosed = await Browser.addListener('browserFinished', () => {
				this.accountPageClosed?.remove();
				this.accountPageClosed = null;
				this.zone.run(closed);
			});
		}
		await Browser.open({ url: `${environment.idHost}${path}` });
	}

	/** Exchanges the code Pro Pool ID sent back for tokens, after checking it answers this app's own login. */
	async completeLogin(callbackUrl: string): Promise<LoginResponse> {
		const params = new URL(callbackUrl).searchParams;
		const pending = JSON.parse(sessionStorage.getItem(PENDING_LOGIN) ?? 'null');
		sessionStorage.removeItem(PENDING_LOGIN);
		if (params.get('error')) {
			throw new IdTokenError(params.get('error'));
		}
		if (!pending || !params.get('code') || params.get('state') !== pending.state) {
			throw new IdTokenError('invalid_state');
		}
		return this.token({
			grant_type: 'authorization_code',
			code: params.get('code'),
			redirect_uri: this.redirectUri,
			code_verifier: pending.verifier,
		});
	}

	/** Pro Pool ID rotates refresh tokens: the answer carries a new one and the old one is spent. */
	refresh(refreshToken: string): Promise<LoginResponse> {
		return this.token({ grant_type: 'refresh_token', refresh_token: refreshToken });
	}

	/** Revokes the refresh token and ends the session at Pro Pool ID, so the next login asks for credentials again. */
	async endSession(refreshToken: string | null): Promise<void> {
		if (refreshToken) {
			// Best effort: logging out must not depend on it.
			await fetch(`${environment.idHost}connect/revoke`, {
				method: 'POST',
				body: new URLSearchParams({ client_id: CLIENT_ID, token: refreshToken, token_type_hint: 'refresh_token' }),
			}).catch(() => undefined);
		}
		const query = new URLSearchParams({
			client_id: CLIENT_ID,
			post_logout_redirect_uri: this.native ? NATIVE_LOGOUT_REDIRECT : `${environment.currentHost}/`,
		});
		await this.open(`${environment.idHost}connect/logout?${query}`, NATIVE_LOGOUT_REDIRECT);
	}

	private async token(body: Record<string, string>): Promise<LoginResponse> {
		const response = await fetch(`${environment.idHost}connect/token`, {
			method: 'POST',
			body: new URLSearchParams({ client_id: CLIENT_ID, ...body }),
		});
		const json = await response.json().catch(() => ({}));
		if (!response.ok || !json.access_token) {
			throw new IdTokenError(json.error ?? `http_${response.status}`);
		}
		return { access_token: json.access_token, refresh_token: json.refresh_token ?? null };
	}

	private async open(url: string, nativeRedirect: string): Promise<string | null> {
		if (!this.native) {
			this.navigate(url);
			return null;
		}

		await this.redirectListener?.remove();
		let redirected: (url: string) => void;
		const callback = new Promise<string>(resolve => redirected = resolve);
		// Capacitor calls back outside Angular's zone; re-enter it so whatever the caller dispatches renders.
		this.redirectListener = await App.addListener('appUrlOpen', ({ url: opened }) => {
			if (opened.startsWith(nativeRedirect)) {
				this.zone.run(() => redirected(opened));
			}
		});
		await Browser.open({ url });
		const result = await callback;
		await this.redirectListener?.remove();
		this.redirectListener = null;
		await Browser.close().catch(() => undefined);
		return result;
	}

	// Separate so tests can replace the page navigation.
	navigate(url: string): void {
		window.location.assign(url);
	}
}

function randomString(): string {
	return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

async function codeChallenge(verifier: string): Promise<string> {
	return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

function base64Url(bytes: Uint8Array): string {
	return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
